import React from 'react';
import { T, NUMERIC, money } from '../customer/customers.theme';
import { Dialog, DialogHeader, ErrorLine, GhostButton, Spinner } from '../customer/CustomersUI';
import { Thumbnail } from './MenuUI';
import { initialsOf } from './menuApi';

/**
 * Confirm before an item leaves the menu.
 *
 * The phone says "this menuItem will be deleted permanently", which is not what
 * happens: the server marks it unavailable and stops listing it, because orders
 * already placed still point at it and a receipt printed last week has to keep
 * its line. The wording here says what the server actually does.
 */

const COPY = {
  title: 'Are you sure?',
  body: 'This item will be taken off the menu. Orders that already include it are not changed.',
  cancel: 'Cancel',
  confirm: 'Delete',
  failed: 'Could not delete. Try again.',
};

export default function DeleteMenuItemDialog({ item, currency, onClose, onConfirm }) {
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');

  const confirm = async () => {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      await onConfirm(item);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || COPY.failed);
      setBusy(false);
    }
  };

  return (
    <Dialog onClose={onClose} locked={busy} width={400}>
      <div style={{ padding: '22px 24px 24px' }}>
        <DialogHeader title={COPY.title} onClose={onClose} locked={busy} />

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            marginTop: 18,
            padding: 14,
            background: T.sunk,
            border: `1px solid ${T.border}`,
            borderRadius: T.radiusControl,
          }}
        >
          <div style={{ width: 54, flexShrink: 0 }}>
            <Thumbnail
              src={item.imageUrl}
              initials={initialsOf(item.name)}
              height={54}
              radius={T.radiusControl}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
            <span
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: T.ink,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {item.name}
            </span>
            <span style={{ fontSize: 13, fontWeight: 700, color: T.brand, ...NUMERIC }}>
              {money(item.price, currency)}
            </span>
          </div>
        </div>

        <div style={{ fontSize: 12.5, color: T.inkSoft, lineHeight: 1.6, paddingTop: 16 }}>
          {COPY.body}
        </div>

        <div style={{ paddingTop: 16 }}>
          <ErrorLine>{error}</ErrorLine>
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: error ? 12 : 20 }}>
          <GhostButton style={{ flex: 1 }} onClick={onClose} disabled={busy}>
            {COPY.cancel}
          </GhostButton>
          <button
            type="button"
            onClick={confirm}
            disabled={busy}
            style={{
              display: 'flex',
              flex: 1,
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              height: 48,
              background: T.surface,
              border: `1px solid ${T.dangerBorder}`,
              borderRadius: 10,
              font: 'inherit',
              fontSize: 14,
              fontWeight: 700,
              color: T.danger,
              opacity: busy ? 0.6 : 1,
              cursor: busy ? 'not-allowed' : 'pointer',
            }}
          >
            {busy ? <Spinner size={14} /> : null}
            <span>{COPY.confirm}</span>
          </button>
        </div>
      </div>
    </Dialog>
  );
}
